import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import { articleIcon, domainIcon, validateIcon } from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { domainSelector, useOffreService } from '@europrocurement/l2d-domain';
import { CategorieDArticleJsonldCategorieArticleAll } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { castIriToId } from '@europrocurement/l2d-utils';

import Form from './Form';
import { articleCategoryFichePath, articleCategoryListPath } from './routes';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const ARTICLE_CATEGORY_SECTION_LABEL = 'Configuration comptabilité';

/**
 * Fiche d'une catégorie d'article sur route dédiée.
 * - `admin/configuration-comptabilite/categorie-article/new` : création.
 * - `admin/configuration-comptabilite/categorie-article/:id` : édition (chargée par identifiant).
 */
const ArticleCategoryFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { articleCategoryModel: model } = useOffreService();
    const { request } = useApiRequest<CategorieDArticleJsonldCategorieArticleAll>();
    const fetchedDomainsOptions = useSelector(domainSelector).main.data;

    const [entity, setEntity] = useState<CategorieDArticleJsonldCategorieArticleAll | undefined>(
        undefined,
    );
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Libellé saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;
        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isCreate]);

    const goToList = useCallback(() => {
        navigate(articleCategoryListPath);
    }, [navigate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(articleCategoryFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, model, navigate, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'une catégorie d'article", draftLabel)
        : (entity?.libelle ?? "Catégorie d'article");

    const domainLabel = ((): string | undefined => {
        if (!entity?.domaine) {
            return undefined;
        }
        const domainId = Number(castIriToId(entity.domaine));
        return fetchedDomainsOptions.find((domain) => domain.id === domainId)?.libelle ?? undefined;
    })();

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  {
                      icon: domainIcon.props.icon,
                      label: domainLabel ? `Domaine : ${domainLabel}` : undefined,
                  },
                  {
                      icon: validateIcon.props.icon,
                      label: entity.validatorType?.label
                          ? `Validateur : ${entity.validatorType.label}`
                          : undefined,
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Catégorie d'article introuvable"
                        subText="Cette catégorie n'existe pas ou n'a pas pu être chargée."
                    />
                </Box>
            );
        }

        return (
            <Form
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onCancel={goToList}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={articleIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={ARTICLE_CATEGORY_SECTION_LABEL}
                listPath={articleCategoryListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default ArticleCategoryFiche;
